import { type LookupValue } from '@onegov/react-component-library';
import { ErrorMessages } from '@snsw/react-component-library';
import { type FieldPath, type FieldValues, type RegisterOptions, type ValidationValueMessage } from 'react-hook-form';

// Type alias for the rules property as defined in react-hook-forms
type FieldRules<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<RegisterOptions<TFieldValues, TName>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;

// Helper function to remap the 'required' rule for boolean options, since 'false' will artificially
// trigger a failure
export const remapRequiredRule = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  options: LookupValue<unknown>[],
  rules?: FieldRules<TFieldValues, TName>
): FieldRules<TFieldValues, TName> => {
  if (!rules) return {};

  const { required, validate = {}, ...otherRules } = rules;
  if (!required) return rules;

  const isBoolean = options.some(({ value }) => typeof value === 'boolean');
  if (!isBoolean) return rules;

  const requiredMessage =
    typeof required === 'string'
      ? required
      : (required as ValidationValueMessage).message || ErrorMessages.getErrorMessage('required');

  return {
    ...otherRules,
    validate: {
      ...validate,
      required: (value?: boolean) => (value != null ? undefined : requiredMessage),
    },
  };
};
